import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  autoUpdate,
  FloatingFocusManager,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { subMonths } from 'date-fns';
import { useSelector } from 'react-redux';
import {
  CheckBoxData,
  ColorType,
  DateRangePicker,
  getHexColorByType,
  Label,
  LabelType,
} from '../index';

import {
  formatDateRange,
  formatDateTimeRange,
  getLastFifteenMin,
  getLastMonth,
  getLastOneDay,
  getLastOneHrs,
  getLastThreeDay,
  getLastThreeHrs,
  getLastWeek,
} from '../../../utils/Date.Util';

import { Calendar, ChevronRight } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { getCurrentTheme } from '../../../stores/selectors/theme.selector';
import { Icon } from '../Icon.component';
import { SparkDateTimePicker } from './SparkDateTimePicker.component';

export enum DATE_RANGE {
  LAST_HOUR = 'LAST_HOUR',
  LAST_THREE_HOUR = 'LAST_THREE_HOUR',
  LAST_FIFTEEN_MIN = 'LAST_FIFTEEN_MIN',
  LAST_ONE_HOUR = 'LAST_ONE_HOUR',
  LAST_ONE_DAY = 'LAST_ONE_DAY',
  LAST_THREE_DAY = 'LAST_THREE_DAY',
  LAST_WEEK = 'LAST_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  CUSTOM_RANGE = 'CUSTOM_RANGE',
}

export enum DATE_PICKER_TYPE {
  DATE_RANGE_PICKER = 'DATE_RANGE_PICKER',
  DATE_TIME_PICKER = 'DATE_TIME_PICKER',
}

export interface InputProps {
  onChange?: (option: string, date: any) => void;
  format?: string;
  optionList: Array<DATE_RANGE>;
  selectedOption: string;
  selectedDate?: Date[];
  showHighligtHeader?: boolean;
  placeholder?: string;
  dataTestId?: string;
  includeDays?: number;
  pickerType?: DATE_PICKER_TYPE;
  timeZone?: string;
}

const datePickerdefaultOptions = [
  {
    label: 'date_picker_last_fifteen_minutes',
    selected: false,
    id: DATE_RANGE.LAST_FIFTEEN_MIN,
  },
  {
    label: 'date_picker_last_hour',
    selected: false,
    id: DATE_RANGE.LAST_ONE_HOUR,
  },
  {
    label: 'date_picker_last_three_hour',
    selected: false,
    id: DATE_RANGE.LAST_THREE_HOUR,
  },
  {
    label: 'date_picker_last_day',
    selected: false,
    id: DATE_RANGE.LAST_ONE_DAY,
  },
  {
    label: 'date_picker_last_three_day',
    selected: false,
    id: DATE_RANGE.LAST_THREE_DAY,
  },
  {
    label: 'date_picker_last_week',
    selected: false,
    id: DATE_RANGE.LAST_WEEK,
  },
  {
    label: 'date_picker_last_month',
    selected: false,
    id: DATE_RANGE.LAST_MONTH,
  },
  {
    label: 'date_picker_custom_range',
    selected: false,
    id: DATE_RANGE.CUSTOM_RANGE,
  },
];

// eslint-disable-next-line react/display-name
export const CustomDatePicker = memo(
  ({
    onChange,
    format,
    optionList,
    selectedOption = DATE_RANGE.LAST_MONTH,
    selectedDate,
    showHighligtHeader = true,
    placeholder,
    dataTestId = '',
    includeDays,
    pickerType = DATE_PICKER_TYPE.DATE_RANGE_PICKER,
    timeZone,
  }: InputProps) => {
    const [isDropDownOpen, setIsDropDownOpen] = useState(false);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const dropDownWrapperRef = useRef<HTMLDivElement>(null);
    const dateRangeWrapperRef = useRef<HTMLDivElement>(null);
    const allowFocusAfterInitRef = useRef(false);
    const isKeyboardSelectingRef = useRef(false);

    const selectedDefaultOptions = useMemo(() => {
      return datePickerdefaultOptions?.map((item) => {
        if (item.id === selectedOption) {
          return { ...item, selected: true };
        }
        return item;
      });
    }, [selectedOption]);

    const defaultOptions = selectedDefaultOptions?.filter((item) => optionList?.includes(item.id));

    const { t } = useTranslation();

    const [selectedRange, setSelectedRange] = useState<(Date | null)[]>([
      subMonths(new Date(), 1),
      null,
    ]);

    // Updates selected range when direct url with search params is opened
    useEffect(() => {
      if (selectedDate?.length === 2 && selectedDate?.[0] && selectedDate?.[1]) {
        setSelectedRange(selectedDate);
      }
    }, [selectedDate]);

    const [options, setOptions] = useState<CheckBoxData[]>(defaultOptions);

    // Updates selected range when direct url with search params is opened
    useEffect(() => {
      setOptions(
        defaultOptions.map((option) => {
          return {
            ...option,
            selected: selectedOption === option.id,
          };
        }),
      );
      // eslint-disable-next-line
    }, [selectedOption]);

    const toggleMenu = (val: boolean) => {
      setIsDropDownOpen(val);
    };

    const openPicker = () => {
      setIsPickerOpen(true);
      setIsDropDownOpen(false);
    };

    const closePicker = () => {
      setIsPickerOpen(false);
    };

    useEffect(() => {
      if (selectedRange?.length === 2 && selectedRange[0] && selectedRange[1]) {
        onChange &&
          onChange(
            options.filter((option: CheckBoxData) => option.selected === true)[0]?.id ||
              DATE_RANGE.LAST_MONTH,
            selectedRange,
          );

        pickerType === DATE_PICKER_TYPE.DATE_RANGE_PICKER && closePicker();
      }
      // eslint-disable-next-line
    }, [selectedRange]);

    // Accessibility Logic: Focuses the selected start date on custom date reange calendar to enable end date selection with Keyboard
    useEffect(() => {
      if (isPickerOpen && selectedRange[0] && !selectedRange[1]) {
        setTimeout(() => {
          const formattedDay = selectedRange[0]?.getDate()?.toString().padStart(2, '0');

          const dayToFocus = document.querySelector(
            `.react-datepicker__day--selecting-range-start.react-datepicker__day--0${formattedDay}`,
          ) as HTMLElement;

          dayToFocus?.focus();
        }, 100);
      }
    }, [isPickerOpen, selectedRange]);

    // Accessibility Logic:
    // 1) Tracks whether the user is selecting a date using the Enter key,
    //    so customDateChangedwithKeyboard can apply the correct logic.
    // 2) When user pressed ESC key on custom range calendar UI, the UI is closed.
    const triggerKeyboardSelect = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        isKeyboardSelectingRef.current = true;
      }
      if (e.key === 'Escape') {
        setIsPickerOpen(false);
      }
    };

    const customDateChanged = (date: Date[]) => {
      setSelectedRange(date);
    };

    // Accessibility Logic: When selecting a date range using the keyboard,
    // the react-datepicker library includes an extra day in the end date.
    // This logic adjusts for that by subtracting one day.
    const customDateChangedwithKeyboard = (date: Date[]) => {
      const ONE_DAY = 24 * 60 * 60 * 1000;

      if (isKeyboardSelectingRef.current && date.length === 2 && date[0] && date[1]) {
        const correctedEnd = new Date(date[1].getTime() - ONE_DAY);
        setSelectedRange([date[0], correctedEnd]);
      } else {
        setSelectedRange(date);
      }
      allowFocusAfterInitRef.current = false;
      isKeyboardSelectingRef.current = false;
    };

    const optionChanged = (val: CheckBoxData) => {
      const newOptions = options.map((option) => {
        return {
          ...option,
          selected: option.id === val.id,
        };
      });
      setOptions([...newOptions]);

      switch (val?.id) {
        case DATE_RANGE.LAST_FIFTEEN_MIN: {
          setSelectedRange(getLastFifteenMin());
          break;
        }

        case DATE_RANGE.LAST_ONE_HOUR: {
          setSelectedRange(getLastOneHrs());
          break;
        }

        case DATE_RANGE.LAST_THREE_HOUR: {
          setSelectedRange(getLastThreeHrs());
          break;
        }

        case DATE_RANGE.LAST_ONE_DAY: {
          setSelectedRange(getLastOneDay());
          break;
        }

        case DATE_RANGE.LAST_THREE_DAY: {
          setSelectedRange(getLastThreeDay());
          break;
        }

        case DATE_RANGE.LAST_WEEK: {
          setSelectedRange(getLastWeek());
          break;
        }
        case DATE_RANGE.LAST_MONTH: {
          setSelectedRange(getLastMonth());
          break;
        }
        case DATE_RANGE.CUSTOM_RANGE: {
          if (
            pickerType === DATE_PICKER_TYPE.DATE_TIME_PICKER &&
            selectedDate?.length === 2 &&
            selectedDate?.[0] &&
            selectedDate?.[1]
          ) {
            setSelectedRange(selectedDate);
          } else {
            setSelectedRange([null, null]);
          }

          openPicker();
          break;
        }

        default:
      }

      setIsDropDownOpen(false);
    };

    const getHeaderText = () => {
      const selectedId = options.find((o) => o.selected)?.id;
      switch (selectedId) {
        case DATE_RANGE.LAST_FIFTEEN_MIN:
          return t('date_picker_last_fifteen_min');
        case DATE_RANGE.LAST_ONE_HOUR:
          return t('date_picker_last_hour');
        case DATE_RANGE.LAST_THREE_HOUR:
          return t('date_picker_last_three_hour');
        case DATE_RANGE.LAST_ONE_DAY:
          return t('date_picker_last_day');
        case DATE_RANGE.LAST_THREE_DAY:
          return t('date_picker_last_three_day');
        case DATE_RANGE.LAST_WEEK:
          return t('date_picker_last_week');
        case DATE_RANGE.LAST_MONTH:
          return t('date_picker_last_month');
        case DATE_RANGE.CUSTOM_RANGE:
          if (pickerType === DATE_PICKER_TYPE.DATE_TIME_PICKER) {
            return selectedRange?.length > 0
              ? formatDateTimeRange(selectedRange)
              : t('date_picker_custom_range');
          }
          return selectedRange?.length > 0
            ? formatDateRange(selectedRange)
            : t('date_picker_custom_range');

        default:
          console.warn('unknown range id');
          return 'Select time range';
      }
    };

    const onPickerClicked = () => {
      const newOptions = options.map((option) => {
        return {
          ...option,
          selected: option.id === DATE_RANGE.CUSTOM_RANGE,
        };
      });
      setOptions(newOptions);
      openPicker();
    };

    const renderCustomRanngeButton = () => {
      const selectedOptionId = options.find((o) => o.selected)?.id;
      if (selectedOptionId !== DATE_RANGE.CUSTOM_RANGE) {
        return null;
      }
      return (
        <button
          className='flex flex-row items-center justify-between text-left py-[10px] px-3 w-full h-[40px] bg-grey1 outline-0 cursor-pointer'
          onClick={onPickerClicked}
        >
          <Label
            text={
              pickerType === DATE_PICKER_TYPE.DATE_TIME_PICKER
                ? formatDateTimeRange(selectedRange)
                : formatDateRange(selectedRange)
            }
            type={LabelType.BODY4}
            color={ColorType.BLACK}
          />
          <Icon src={ChevronRight} size={IconSize.SIZE_20x20} />
        </button>
      );
    };

    const theme = useSelector(getCurrentTheme);

    // Accessibility Logic: Enables navigation and selection of dropdown items using the keyboard
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    const dropDownKeyDown = (e: React.KeyboardEvent, index: number) => {
      const prevIndex = index - 1;

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          optionChanged(options[index]);
          setTimeout(() => setIsDropDownOpen(false), 0);
          allowFocusAfterInitRef.current = false;
          break;

        case 'ArrowDown':
        case 'Tab': {
          e.preventDefault();
          const moveIndex = e.shiftKey ? index - 1 : index + 1;
          if (itemRefs.current[moveIndex]) {
            setTimeout(() => {
              itemRefs.current[moveIndex]?.focus();
            }, 0);
          }
          break;
        }

        case 'ArrowUp':
          e.preventDefault();
          if (itemRefs.current[prevIndex]) {
            itemRefs.current[prevIndex]?.focus();
          }
          break;

        case 'Escape':
          setIsDropDownOpen(false);
          allowFocusAfterInitRef.current = false;
          break;

        default:
          break;
      }
    };

    const renderDropdown = () => {
      return (
        <>
          {options.map((option, index) => {
            return (
              <div
                key={`option-key-${option.id}`}
                className='w-full py-2 pl-2 cursor-pointer hover:bg-grey1 outline-0 focus:outline focus:outline-2 focus:outline-[#0367CC]'
                onClick={() => optionChanged(option)}
                style={{
                  backgroundColor:
                    option.selected && option.id !== DATE_RANGE.CUSTOM_RANGE
                      ? getHexColorByType(ColorType.GREY1)
                      : '',
                }}
                role='button'
                tabIndex={0}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                onKeyDown={(e) => dropDownKeyDown(e, index)}
              >
                <Label
                  className='outline-0'
                  type={LabelType.BODY3}
                  color={ColorType.BLACK}
                  text={t(option.label)}
                  style={{
                    color:
                      option.selected && option.id !== DATE_RANGE.CUSTOM_RANGE
                        ? theme.colorPalettes.primary
                        : getHexColorByType(ColorType.BLACK),
                  }}
                />
              </div>
            );
          })}
          {pickerType === DATE_PICKER_TYPE.DATE_RANGE_PICKER && renderCustomRanngeButton()}
        </>
      );
    };

    const handleClickDateRangeOutSide = useCallback(() => {
      if (isPickerOpen) {
        setIsPickerOpen(false);
        // means only start range selected, set default option and set select range to last month
        if (selectedRange[1] === null) {
          setOptions(defaultOptions);
          setSelectedRange(getLastMonth());
        }
      }
    }, [isPickerOpen, defaultOptions, selectedRange]);

    const renderDateRangePicker = useMemo(() => {
      if (pickerType === DATE_PICKER_TYPE.DATE_RANGE_PICKER) {
        return (
          <DateRangePicker
            defaultRange={selectedRange}
            onChange={customDateChangedwithKeyboard}
            format={format}
            monthsShown={2}
            inline
            onClickOutSide={handleClickDateRangeOutSide}
            placeholder={placeholder}
            includeDays={includeDays}
          />
        );
      }
      return (
        <div className='absolute py-2 z-50'>
          <SparkDateTimePicker
            onClickOutSide={handleClickDateRangeOutSide}
            onChange={customDateChanged}
            timeZone={timeZone}
          />
        </div>
      );
    }, [
      format,
      handleClickDateRangeOutSide,
      includeDays,
      pickerType,
      selectedRange,
      timeZone,
      placeholder,
    ]);

    const { refs, floatingStyles, context } = useFloating({
      placement: 'bottom-start',
      open: isDropDownOpen,
      onOpenChange: (val) => {
        toggleMenu(val);
      },
      middleware: [
        offset(10),
        size({
          apply({ rects, elements, availableHeight }) {
            Object.assign(elements.floating.style, {
              maxHeight: `${availableHeight}px`,
              minWidth: `${rects.reference.width}px`,
            });
          },
          padding: 10,
        }),
      ],
      whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    const renderHeaderInput = () => {
      const selectedOptionId = options.find((o) => o.selected)?.id;
      const isHighLight =
        showHighligtHeader &&
        (selectedOptionId === DATE_RANGE.CUSTOM_RANGE ||
          selectedOptionId === DATE_RANGE.LAST_WEEK ||
          selectedOptionId === DATE_RANGE.LAST_FIFTEEN_MIN ||
          selectedOptionId === DATE_RANGE.LAST_ONE_HOUR ||
          selectedOptionId === DATE_RANGE.LAST_ONE_DAY ||
          selectedOptionId === DATE_RANGE.LAST_THREE_DAY ||
          selectedOptionId === DATE_RANGE.LAST_THREE_HOUR);

      return (
        <div
          ref={refs.setReference}
          {...getReferenceProps()}
          className={`flex h-10 rounded  items-center cursor-pointer ${
            isHighLight ? 'bg-grey6' : 'bg-grey1 hover:bg-grey2 '
          }`}
          data-testid={dataTestId}
        >
          <Icon
            src={Calendar}
            className='ml-4'
            size={IconSize.SIZE_20x20}
            color={isHighLight ? ColorType.WHITE : ''}
            alt={t('icon_alt_time_selection_drop_down')}
          />
          <Label
            text={getHeaderText()}
            type={LabelType.LABEL_S_MEDIUM}
            color={isHighLight ? ColorType.WHITE : ColorType.GREY6}
            className='ml-[8px] pr-4'
          />
        </div>
      );
    };

    // Accessibility Logic: Enables keyboard focus after dropdown is closed with keyboard
    useEffect(() => {
      allowFocusAfterInitRef.current = true;
    }, []);

    useEffect(() => {
      if (!allowFocusAfterInitRef.current && !isDropDownOpen && dropDownWrapperRef.current) {
        dropDownWrapperRef.current.focus();
      }
    }, [isDropDownOpen, selectedRange]);

    // Accessibility Logic: Enables keyboard focus on the custom range calendar
    useEffect(() => {
      if (isPickerOpen && dateRangeWrapperRef.current) {
        dateRangeWrapperRef.current.focus();
      }
    }, [isPickerOpen]);

    // Accessibility Logic: Enables opening or closing the dropdown menu with keyboard keys
    const OpenPickerOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isPickerOpen) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsDropDownOpen(true);
      }

      if (e.key === 'Escape') {
        setIsDropDownOpen(false);
      }
    };

    return (
      <div
        role='button'
        className='cursor-default'
        tabIndex={0}
        onKeyDown={OpenPickerOnKeyDown}
        ref={dropDownWrapperRef}
      >
        <div>
          {renderHeaderInput()}
          {isPickerOpen && (
            <div
              tabIndex={-1}
              className='absolute z-50 min-w-fit'
              onKeyDown={triggerKeyboardSelect}
              ref={dateRangeWrapperRef}
            >
              <div className='relative min-w-fit'>{renderDateRangePicker}</div>
            </div>
          )}
        </div>
        {isDropDownOpen && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              className='z-50 outline-0 p-2 list-shadow rounded bg-white w-[200px]'
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {renderDropdown()}
            </div>
          </FloatingFocusManager>
        )}
      </div>
    );
  },
);
